import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import ChaletReassignPanel from "./ChaletReassignPanel";

// Per-chalet occupancy overview for managers. The week calendar above shows "1 ledig" per day,
// but that is a day-by-day count: the free chalet can be a DIFFERENT one on different days
// (same-day turnover). This overview shows every physical chalet across a period so a manager
// can see - before clicking - whether one single chalet is free for the whole stay.

type ChaletType = "Big" | "Small";

interface BookingDetail {
  bookingId: number;
  chaletId: number;
  arrival: number; // day number (UTC days since epoch)
  departure: number;
  customerName: string;
  packageName: string;
  arrivalText: string;
  departureText: string;
}

interface ChaletRow {
  id: number;
  type: ChaletType;
  bookings: BookingDetail[];
}

const DAY_MS = 86400000;
const WEEKDAYS = ["sön", "mån", "tis", "ons", "tors", "fre", "lör"];

const dayNum = (iso: string): number => {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / DAY_MS);
};
const dayToIso = (n: number): string => new Date(n * DAY_MS).toISOString().slice(0, 10);
const dayLabel = (n: number): string => {
  const dt = new Date(n * DAY_MS);
  return `${dt.getUTCDate()}/${dt.getUTCMonth() + 1}`;
};
const dayWeekday = (n: number): string => WEEKDAYS[new Date(n * DAY_MS).getUTCDay()];

const parseType = (value: unknown): ChaletType | null => {
  const t = String(value).toLowerCase().trim();
  if (t === "big" || t === "0") return "Big";
  if (t === "small" || t === "1") return "Small";
  return null;
};

const typeName = (t: ChaletType) => (t === "Big" ? "Stor stuga" : "Liten stuga");

interface Props {
  apiUrl: string;
  viewStart: Date | null;
}

const ChaletOccupancyOverview: React.FC<Props> = ({ apiUrl, viewStart }) => {
  const [open, setOpen] = useState(true);
  const [startIso, setStartIso] = useState<string>("");
  const [days, setDays] = useState<number>(14);
  const [stayArrival, setStayArrival] = useState<string>("");
  const [stayDeparture, setStayDeparture] = useState<string>("");
  const [rows, setRows] = useState<ChaletRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Follow the week the manager navigates to in the calendar above
  const viewStartIso = viewStart
    ? `${viewStart.getFullYear()}-${String(viewStart.getMonth() + 1).padStart(2, "0")}-${String(viewStart.getDate()).padStart(2, "0")}`
    : "";
  useEffect(() => {
    if (!viewStartIso) return;
    setStartIso(viewStartIso);
    setStayArrival(viewStartIso);
    setStayDeparture(dayToIso(dayNum(viewStartIso) + 7));
  }, [viewStartIso]);

  const startDay = startIso ? dayNum(startIso) : null;
  const endDay = startDay !== null ? startDay + days : null; // exclusive

  useEffect(() => {
    if (startDay === null || endDay === null) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("accessToken");
        const response = await axios.get<any>(`${apiUrl}/PackageBooking/calendar-availability`, {
          params: { startDate: dayToIso(startDay), endDate: dayToIso(endDay) },
          headers: token ? { Authorization: "Bearer " + token } : {},
        });
        const result: ChaletRow[] = [];
        for (const item of response.data?.availabilityByType ?? []) {
          const type = parseType(item.chaletType);
          if (!type) continue;
          const ids: number[] = Array.from(
            new Set<number>([...(item.availableChaletIds ?? []), ...(item.bookedChaletIds ?? [])])
          ).sort((a, b) => a - b);
          for (const id of ids) {
            result.push({ id, type, bookings: [] });
          }
          for (const b of item.bookingDetails ?? []) {
            const row = result.find((r) => r.id === b.chaletId);
            if (!row) continue;
            row.bookings.push({
              bookingId: b.bookingId,
              chaletId: b.chaletId,
              arrival: dayNum(String(b.arrivalDate)),
              departure: dayNum(String(b.departureDate)),
              customerName: b.customerName,
              packageName: b.packageName,
              arrivalText: String(b.arrivalDate).slice(0, 10),
              departureText: String(b.departureDate).slice(0, 10),
            });
          }
        }
        result.sort((a, b) => (a.type === b.type ? a.id - b.id : a.type === "Big" ? -1 : 1));
        if (!cancelled) setRows(result);
      } catch (e: any) {
        if (!cancelled) {
          setRows([]);
          setError(e?.response?.data?.message || e?.message || "Kunde inte hämta stugöversikten.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, startDay, endDay, reloadKey]);

  // A chalet is free for the stay [arrival, departure) when no booking overlaps it.
  // Same-day turnover: a booking that departs ON the arrival day (or arrives ON the departure
  // day) does not overlap, which is exactly what arrival < dep && departure > arr expresses.
  const isFreeFor = (row: ChaletRow, arr: number, dep: number) =>
    !row.bookings.some((b) => b.arrival < dep && b.departure > arr);

  const freeIds = (type: ChaletType, arr: number, dep: number) =>
    rows.filter((r) => r.type === type && isFreeFor(r, arr, dep)).map((r) => r.id);

  const totals = useMemo(
    () => ({
      Big: rows.filter((r) => r.type === "Big").length,
      Small: rows.filter((r) => r.type === "Small").length,
    }),
    [rows]
  );

  const dayList = useMemo(() => {
    if (startDay === null) return [];
    return Array.from({ length: days }, (_, i) => startDay + i);
  }, [startDay, days]);

  const stayArr = stayArrival ? dayNum(stayArrival) : null;
  const stayDep = stayDeparture ? dayNum(stayDeparture) : null;
  const stayValid = stayArr !== null && stayDep !== null && stayDep > stayArr;
  const stayInsideGrid =
    stayValid && startDay !== null && endDay !== null && stayArr! >= startDay && stayDep! <= endDay;

  const weekBlocks = useMemo(() => {
    if (startDay === null) return [];
    const blocks: { from: number; to: number }[] = [];
    for (let s = startDay; s + 7 <= startDay + days; s += 7) blocks.push({ from: s, to: s + 7 });
    return blocks;
  }, [startDay, days]);

  const bookingsText = (row: ChaletRow, day: number) => {
    const hits = row.bookings.filter((b) => b.arrival <= day && b.departure >= day);
    if (hits.length === 0) return `Stuga ${row.id}: ledig`;
    return hits
      .map(
        (b) =>
          `Stuga ${row.id}: ${b.customerName} - ${b.packageName} (${b.arrivalText} → ${b.departureText}, bokning #${b.bookingId})`
      )
      .join("\n");
  };

  const BUSY = "#ef9a9a";
  const FREE = "#c8e6c9";

  const badge = (type: ChaletType, count: number, onClick?: () => void) => (
    <span
      key={type}
      onClick={onClick}
      style={{
        display: "inline-block",
        padding: "1px 8px",
        marginRight: 6,
        borderRadius: 10,
        fontSize: 12,
        fontWeight: 600,
        cursor: onClick ? "pointer" : "default",
        color: count > 0 ? "#1b5e20" : "#b71c1c",
        background: count > 0 ? "#e8f5e9" : "#ffebee",
        border: `1px solid ${count > 0 ? "#81c784" : "#e57373"}`,
      }}
    >
      {type === "Big" ? "Stor" : "Liten"} {count}/{totals[type]}
    </span>
  );

  return (
    <div className="mb-3" style={{ border: "1px solid #dee2e6", borderRadius: 6, background: "#fff" }}>
      <div
        className="d-flex align-items-center justify-content-between px-3 py-2"
        style={{ cursor: "pointer", background: "#f8f9fa", borderRadius: 6 }}
        onClick={() => setOpen(!open)}
      >
        <strong>Stugöversikt - vilka stugor är lediga HELA perioden?</strong>
        <span>{open ? "▲ Dölj" : "▼ Visa"}</span>
      </div>

      {open && (
        <div className="p-3" style={{ fontSize: 13 }}>
          <div className="mb-2 text-muted">
            Kalendern ovan visar antal lediga stugor <em>per dag</em>. Det kan vara olika stugor olika dagar. Här ser du varje
            stuga för sig: en bokning kräver att <strong>samma stuga</strong> är ledig alla nätter. Bytesdag
            (utcheckning före 12, incheckning efter 12) räknas som ledig.
          </div>

          <div className="d-flex flex-wrap align-items-end mb-3" style={{ gap: 12 }}>
            <div>
              <label className="d-block mb-0">Visa från</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={startIso}
                onChange={(e) => e.target.value && setStartIso(e.target.value)}
              />
            </div>
            <div>
              <label className="d-block mb-0">Antal dagar</label>
              <select
                className="form-control form-control-sm"
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
              >
                <option value={7}>7 (1 vecka)</option>
                <option value={14}>14 (2 veckor)</option>
                <option value={21}>21 (3 veckor)</option>
                <option value={28}>28 (4 veckor)</option>
              </select>
            </div>
            <div>
              <label className="d-block mb-0">Ankomst</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={stayArrival}
                onChange={(e) => setStayArrival(e.target.value)}
              />
            </div>
            <div>
              <label className="d-block mb-0">Avresa</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={stayDeparture}
                onChange={(e) => setStayDeparture(e.target.value)}
              />
            </div>
          </div>

          {loading && <div className="text-muted mb-2">Hämtar stugöversikt…</div>}
          {error && <div className="alert alert-danger py-1 px-2">{error}</div>}

          {!loading && !error && rows.length > 0 && (
            <>
              <div
                className="mb-3 p-2"
                style={{
                  borderRadius: 6,
                  border: "1px solid #bdbdbd",
                  background: "#fafafa",
                }}
              >
                {!stayValid && <span>Välj ankomst och avresa (avresa efter ankomst) för att se vad som går att boka.</span>}
                {stayValid && !stayInsideGrid && (
                  <span>Ankomst och avresa måste ligga inom den visade perioden ({dayLabel(startDay!)} - {dayLabel(endDay! - 1)}).</span>
                )}
                {stayInsideGrid && (
                  <>
                    <div className="mb-1">
                      <strong>
                        Vistelse {dayLabel(stayArr!)} → {dayLabel(stayDep!)} ({stayDep! - stayArr!} nätter)
                      </strong>
                    </div>
                    {(["Big", "Small"] as ChaletType[]).map((type) => {
                      const ids = freeIds(type, stayArr!, stayDep!);
                      return (
                        <div key={type} className="mb-1">
                          <span style={{ display: "inline-block", minWidth: 100 }}>{typeName(type)}:</span>
                          {ids.length > 0 ? (
                            <span style={{ color: "#1b5e20", fontWeight: 600 }}>
                              ✔ {ids.length} ledig{ids.length > 1 ? "a" : ""} hela perioden (stuga {ids.join(", ")})
                            </span>
                          ) : (
                            <span style={{ color: "#b71c1c", fontWeight: 600 }}>
                              ✖ Ingen enskild stuga är ledig alla nätter - går inte att boka hela perioden
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>

              <ChaletReassignPanel
                apiUrl={apiUrl}
                bookings={Array.from(
                  new Map(
                    rows.flatMap((r) => r.bookings).map((b) => [b.bookingId, b] as const)
                  ).values()
                )}
                stay={
                  stayInsideGrid
                    ? ((["Big", "Small"] as ChaletType[])
                        .filter((t) => totals[t] > 0 && freeIds(t, stayArr!, stayDep!).length === 0)
                        .map((t) => ({ type: t, arrival: stayArrival, departure: stayDeparture }))[0] ?? null)
                    : null
                }
                onMoved={() => setReloadKey((n) => n + 1)}
              />

              <div className="mb-3">
                <div className="mb-1">
                  <strong>Vecka för vecka</strong>
                </div>
                {weekBlocks.length === 0 && <span className="text-muted">Välj minst 7 dagar.</span>}
                {weekBlocks.map((w) => (
                  <div key={w.from} className="mb-1">
                    <span style={{ display: "inline-block", minWidth: 130 }}>
                      {dayLabel(w.from)} → {dayLabel(w.to)}
                    </span>
                    {(["Big", "Small"] as ChaletType[]).map((type) =>
                      badge(type, freeIds(type, w.from, w.to).length, () => {
                        setStayArrival(dayToIso(w.from));
                        setStayDeparture(dayToIso(w.to));
                      })
                    )}
                  </div>
                ))}
              </div>

              <div style={{ overflowX: "auto" }}>
                <table style={{ borderCollapse: "collapse", fontSize: 11 }}>
                  <thead>
                    <tr>
                      <th style={{ padding: "2px 6px", textAlign: "left", position: "sticky", left: 0, background: "#fff" }}>Stuga</th>
                      {dayList.map((d) => {
                        const inStay = stayInsideGrid && d >= stayArr! && d <= stayDep!;
                        return (
                          <th
                            key={d}
                            style={{
                              minWidth: 34,
                              textAlign: "center",
                              padding: "2px 1px",
                              background: inStay ? "#fff3cd" : "transparent",
                              borderBottom: "1px solid #ccc",
                            }}
                          >
                            <div>{dayWeekday(d)}</div>
                            <div>{dayLabel(d)}</div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => (
                      <tr
                        key={row.id}
                        style={{ borderTop: idx > 0 && rows[idx - 1].type !== row.type ? "3px solid #999" : undefined }}
                      >
                        <td
                          style={{
                            padding: "2px 6px",
                            whiteSpace: "nowrap",
                            position: "sticky",
                            left: 0,
                            background: "#fff",
                            fontWeight: 600,
                          }}
                        >
                          {row.type === "Big" ? "Stor" : "Liten"} {row.id}
                        </td>
                        {dayList.map((d) => {
                          // Morning is occupied by the guest who is leaving that day; the rest of
                          // the day (after 12:00) is occupied by whoever arrives or stays.
                          const morningBusy = row.bookings.some((b) => b.arrival < d && b.departure >= d);
                          const afternoonBusy = row.bookings.some((b) => b.arrival <= d && b.departure > d);
                          return (
                            <td key={d} title={bookingsText(row, d)} style={{ padding: 1 }}>
                              <div style={{ display: "flex", height: 20, border: "1px solid #fff" }}>
                                <div style={{ flex: 1, background: morningBusy ? BUSY : FREE }} />
                                <div style={{ flex: 1, background: afternoonBusy ? BUSY : FREE }} />
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-2 text-muted" style={{ fontSize: 12 }}>
                <span style={{ display: "inline-block", width: 12, height: 12, background: BUSY, verticalAlign: "middle" }} /> upptagen
                <span style={{ display: "inline-block", width: 12, height: 12, background: FREE, verticalAlign: "middle", marginLeft: 10 }} /> ledig
                {" - "}varje dag är delad: vänster halva = förmiddag (före 12, utcheckning), höger halva = eftermiddag/natt
                (incheckning). En dag med röd vänster och grön höger halva är en bytesdag. Håll muspekaren över en ruta för
                gäst och paket.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ChaletOccupancyOverview;
